import {
  IGetPurchasedInquiryDetailsParams,
  IIncreaseWalletDepositPayload,
  IPurchasePackagePayload,
  IWithdrawWalletDepositPayload,
  TRawServiceApiKeys,
} from "@/interfaces";
import { coreClient } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { IPayFinesPayload } from "@/interfaces/inquiries/vehicle";
import { ISavedBill } from "@/lib/myBillsStorage";
import { useQueryClient } from "@tanstack/react-query";

export const useGetInquiries = () => {
  return useQuery({
    queryKey: ["services"],
    queryFn: coreClient.getInquiries,
  });
};

export const useGetInquiry = ({
  inquiry,
  token,
}: {
  inquiry: TRawServiceApiKeys | "";
  token?: string;
}) => {
  return useQuery({
    queryKey: ["inquiry", inquiry, token ?? null],
    queryFn: () => {
      return coreClient.getInquiry({ inquiry, token });
    },
    enabled: !!inquiry,
  });
};

export const useGetSettings = (token?: string) => {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => {
      return coreClient.getSettings(token);
    },
  });
};

export const useGetWalletBalance = (token?: string) => {
  return useQuery({
    queryKey: ["wallet-balance"],
    queryFn: () => {
      return coreClient.getWalletBalance({ token });
    },
    enabled: !!token,
  });
};
export const useGetPurchasedInquiryDetails = ({
  purchasedInquiryId,
  token,
}: IGetPurchasedInquiryDetailsParams) => {
  return useQuery({
    queryKey: ["purchased-inquiry-details", purchasedInquiryId],
    queryFn: () => {
      return coreClient.getPurchasedInquiryDetails({
        token,
        purchasedInquiryId: purchasedInquiryId!,
      });
    },
    enabled: !!token && !!purchasedInquiryId,
  });
};

// export const useIncreaseWalletDepositMutation = (
//   token?: string,
//   bank?: string,
// ) => {
//   return useMutation({
//     mutationFn: async ({
//       payload,
//     }: {
//       payload: IIncreaseWalletDepositPayload;
//     }) => {
//       console.log("bank", bank);
//       return await coreClient.increaseWalletDeposit({ token, payload, bank });
//     },
//   });
// };
export const useIncreaseWalletDepositMutation = (token?: string) => {
  return useMutation({
    mutationFn: async ({
      payload,
      bank,
    }: {
      payload: IIncreaseWalletDepositPayload;
      bank?: string;
    }) => {
      return await coreClient.increaseWalletDeposit({ token, payload, bank });
    },
  });
};
export const useWithdrawWalletDepositMutation = (token?: string) => {
  return useMutation({
    mutationFn: async ({
      payload,
    }: {
      payload: IWithdrawWalletDepositPayload;
    }) => {
      return await coreClient.withdrawWalletDeposit({ token, payload });
    },
  });
};

export const useGetStoredData = (
  token?: string,
  query?: Record<string, string>,
) => {
  return useQuery({
    queryKey: ["stored-data"],
    queryFn: () => {
      return coreClient.getStoredData({ token, query });
    },
    enabled: !!token,
  });
};

export const useGetWalletTransactions = (
  token?: string,
  query?: Record<string, string>,
) => {
  return useQuery({
    queryKey: ["wallet-transactions"],
    queryFn: () => {
      return coreClient.getWalletTransactions({ token, query });
    },
    enabled: !!token,
  });
};

export const useGetPaidBillsHistory = (
  token?: string,
  query?: Record<string, string>,
) => {
  return useQuery({
    queryKey: ["paid-bills-history", query],
    queryFn: () => {
      return coreClient.getPaidBillsHistory({ token, query });
    },
    enabled: !!token,
  });
};

export const useGetMyBills = (
  token?: string,
  query?: Record<string, string>,
) => {
  return useQuery({
    queryKey: ["my-bills", query],
    queryFn: () => coreClient.getMyBills({ token, query }),
    enabled: !!token,
  });
};

export const useCreateMyBillMutation = (token?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      purchase_id: number;
      label?: string;
      reminder_enabled?: boolean;
      reminder_day?: number;
      reminder_interval?: "monthly" | "bimonthly";
    }) => coreClient.createMyBill({ token, payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-bills"] }),
  });
};

export const useUpdateMyBillMutation = (token?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      payload,
    }: {
      billId: string;
      payload: Partial<
        Pick<
          ISavedBill,
          "label" | "reminderEnabled" | "reminderDay" | "reminderInterval"
        >
      >;
    }) => coreClient.updateMyBill({ token, billId, payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-bills"] }),
  });
};

export const useDeleteMyBillMutation = (token?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billId: string) => coreClient.deleteMyBill({ token, billId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-bills"] }),
  });
};

export const useBulkInquiryMyBillsMutation = (token?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billIds: string[]) =>
      coreClient.bulkInquiryMyBills({ token, billIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-bills"] }),
  });
};

export const useGetBanks = () => {
  return useQuery({
    queryKey: ["banks"],
    staleTime: Infinity,
    queryFn: () => {
      return coreClient.getBanksData();
    },
  });
};

export const useGetPackages = () => {
  return useQuery({
    queryKey: ["packages"],
    queryFn: () => {
      return coreClient.getPackagesData();
    },
  });
};

export const usePurchasePackageMutation = () => {
  return useMutation({
    mutationFn: async ({
      purchasedPackageId,
      token,
      bank,
    }: IPurchasePackagePayload) => {
      return coreClient.purchasePackage({
        token,
        purchasedPackageId,
        bank,
      });
    },
  });
};

export const useGetPurchasedPackages = (
  token?: string,
  query?: Record<string, string>,
) => {
  return useQuery({
    queryKey: ["purchased-packages"],
    queryFn: () => {
      return coreClient.getPurchasedPackages({ token, query });
    },
    enabled: !!token,
  });
};

export const usePayFinesMutation = (token?: string) => {
  return useMutation({
    mutationFn: async ({ payload }: { payload: IPayFinesPayload }) => {
      return await coreClient.payFines({ token, payload });
    },
  });
};

export const usePayFinesTokenlessMutation = () => {
  return useMutation({
    mutationFn: async ({ payload }: { payload: IPayFinesPayload }) => {
      return await coreClient.payFinesTokenless({ payload });
    },
  });
};

export const useGetInquiryValidity = (code: string, token?: string) => {
  return useQuery({
    queryKey: ["inquiry-validity"],
    queryFn: () => {
      return coreClient.getInquiryValidity({ token, code });
    },
    enabled: !!code,
  });
};

export const useGetPaidBill = (billId: number, token?: string) => {
  return useQuery({
    queryKey: ["paid-bill"],
    queryFn: () => {
      return coreClient.getPaidBillDetails({ token, billId });
    },
    enabled: !!billId,
  });
};

export const usePurchaseFileMutation = (token?: string) =>
  useMutation({
    mutationFn: ({ payload }: { payload?: any }) =>
      coreClient.purchaseFile({ token: token || "", payload }),
  });
